import {
  Badge,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import CheckIcon from '@material-ui/icons/Check'
import React from 'react'

import {
  atHomeSessionAction,
  fetchMyAtHomeIssues,
  fetchMyAtHomeSessions,
  raiseAtHomeIssue,
  uploadAtHomeSessionPhoto
} from 'api/atHome'
import AlfredButton from 'app/components/generic/Button'
import { Loading } from 'app/components/generic/LottieComponents'
import constants from 'app/constants'
import { eventAlert } from 'app/helpers'

import BookingsAgenda from './BookingsAgenda'
import SlotsCalendar from './SlotsCalendar'

import {
  fmtDate,
  fmtDateTime,
  fmtMoney,
  fmtTime,
  titleCase,
  todayLocal
} from '../format'

/**
 * Teacher → At Home. Everything here is scoped to the logged-in teacher by the
 * backend (/api/v1/oh_at_home/teacher/*): the slots the admin has put against
 * me, the sessions parents have booked, and the issues I have raised.
 */
const useStyles = makeStyles((theme) => ({
  root: { minWidth: 0, padding: theme.spacing(0, 0, 3) },
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingLeft: theme.spacing(2),
    paddingTop: theme.spacing(1),
    minHeight: 56
  },
  tab: {
    minWidth: 'auto',
    minHeight: 56,
    fontSize: 16,
    letterSpacing: 0.4,
    padding: theme.spacing(1.5, 3),
    '&.Mui-selected': { fontWeight: 700 }
  },
  body: { padding: theme.spacing(3) },
  grid: {
    display: 'grid',
    gap: theme.spacing(2),
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))'
  },
  card: { display: 'flex', flexDirection: 'column' },
  content: { flex: 1 },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5)
  },
  label: { color: theme.palette.text.secondary, minWidth: 90 },
  chips: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(1)
  },
  empty: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(4),
    textAlign: 'center'
  },
  safety: { borderLeft: `4px solid ${theme.palette.error.main}` }
}))

// --- Sessions ------------------------------------------------------------------

// One tap, no attendance question (owner, 2026-10-14): a session can only be
// started with the parent's start code at the door, so by the time this is
// reachable the child was there. No-shows are closed from At Home → Attended.
const CompleteDialog = ({ session, onClose, onDone }) => {
  const [busy, setBusy] = React.useState(false)
  const submit = async () => {
    setBusy(true)
    try {
      await atHomeSessionAction(session.uuid, {
        action: 'complete',
        attended: true
      })
      eventAlert('Session marked complete', constants.SUCCESS)
      onDone()
      onClose()
    } catch (error) {
      // toast from request.js
    } finally {
      setBusy(false)
    }
  }
  return (
    <Dialog open={Boolean(session)} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Complete session</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {session?.categoryName} · {session?.childInfo?.name} ·{' '}
          {fmtDate(session?.date)}
        </Typography>
        <Typography variant="body2">
          Done for today? This closes the session; ops see it under Attended
          straight away. Session notes and the photo for the parent are added
          separately, before or after completing.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Back
        </Button>
        <Button
          onClick={submit}
          color="primary"
          variant="contained"
          disabled={busy}
        >
          Mark complete
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// Notes + photo, saved TOGETHER (owner, 2026-10-14): the parent gets both in
// one WhatsApp message 15 minutes after the first save. Mirrors the teacher
// app's "Session notes & photo" card.
const NotesPhotoDialog = ({ session, onClose, onDone }) => {
  const r = session?.report || {}
  const [notes, setNotes] = React.useState(r.notes || '')
  const [file, setFile] = React.useState(null)
  const [preview, setPreview] = React.useState(r.photo_url || '')
  const [busy, setBusy] = React.useState(false)
  const pick = (e) => {
    const f = e.target.files && e.target.files[0]
    if (!f) return
    setFile(f)
    setPreview(URL.createObjectURL(f))
  }
  const canSave =
    notes.trim() &&
    preview &&
    (notes.trim() !== (r.notes || '') || file) &&
    !busy
  const submit = async () => {
    setBusy(true)
    try {
      const url = file ? await uploadAtHomeSessionPhoto(file) : r.photo_url
      await atHomeSessionAction(session.uuid, {
        action: 'summary',
        notes: notes.trim(),
        photo_url: url
      })
      eventAlert(
        r.summary_sent_at
          ? 'Saved'
          : 'Saved. The parent gets the notes and photo on WhatsApp in 15 minutes.',
        constants.SUCCESS
      )
      onDone()
      onClose()
    } catch (error) {
      // toast from request.js
    } finally {
      setBusy(false)
    }
  }
  return (
    <Dialog open={Boolean(session)} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Session notes &amp; photo</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {session?.categoryName} · {session?.childInfo?.name} ·{' '}
          {fmtDate(session?.date)}
        </Typography>
        <TextField
          label="Session notes"
          placeholder="What did you cover? How did the child do? The parent gets this on WhatsApp."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          fullWidth
          multiline
          minRows={4}
          margin="normal"
          variant="outlined"
          InputLabelProps={{ shrink: true }}
        />
        {preview && (
          <img
            src={preview}
            alt="Session"
            style={{
              width: '100%',
              maxHeight: 260,
              objectFit: 'cover',
              borderRadius: 8,
              display: 'block',
              marginBottom: 8
            }}
          />
        )}
        <Button variant="outlined" component="label" size="small">
          {preview ? 'Change photo' : 'Add a photo from the session'}
          <input type="file" accept="image/*" hidden onChange={pick} />
        </Button>
        <Typography
          variant="caption"
          color="textSecondary"
          display="block"
          style={{ marginTop: 12 }}
        >
          {r.summary_sent_at
            ? 'Already sent to the parent on WhatsApp. Changes now show on Alfred only.'
            : r.summary_due_at
            ? 'Saved. The parent gets the notes and photo on WhatsApp 15 minutes after the first save.'
            : 'Both are needed. The parent gets them together on WhatsApp 15 minutes after you save.'}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Back
        </Button>
        <Button
          onClick={submit}
          color="primary"
          variant="contained"
          disabled={!canSave}
        >
          {busy ? 'Saving…' : 'Save notes & photo'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const IssueDialog = ({ session, open, onClose, onDone }) => {
  const [type, setType] = React.useState('operational')
  const [description, setDescription] = React.useState('')
  const [busy, setBusy] = React.useState(false)
  React.useEffect(() => {
    if (open) {
      setType('operational')
      setDescription('')
    }
  }, [open])
  const submit = async () => {
    setBusy(true)
    try {
      await raiseAtHomeIssue({
        type,
        description,
        booking: session ? session.uuid : null
      })
      eventAlert(
        type === 'safety'
          ? 'Safety issue raised — the team has been alerted'
          : 'Issue raised',
        constants.SUCCESS
      )
      onDone()
      onClose()
    } catch (error) {
      // toast from request.js
    } finally {
      setBusy(false)
    }
  }
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Raise an issue</DialogTitle>
      <DialogContent>
        {session && (
          <Typography variant="body2" color="textSecondary" gutterBottom>
            About: {session.categoryName} · {session.childInfo?.name} ·{' '}
            {fmtDate(session.date)} · {session.apartmentName}
          </Typography>
        )}
        <TextField
          select
          label="Type"
          value={type}
          onChange={(e) => setType(e.target.value)}
          fullWidth
          margin="normal"
        >
          <MenuItem value="operational">
            Operational (schedule, access, materials, payment…)
          </MenuItem>
          <MenuItem value="safety">
            Safety (anything that made you or the child unsafe)
          </MenuItem>
        </TextField>
        <TextField
          label="What happened"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          multiline
          minRows={4}
          margin="normal"
          autoFocus
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Back
        </Button>
        <Button
          onClick={submit}
          color={type === 'safety' ? 'secondary' : 'primary'}
          variant="contained"
          disabled={busy || !description.trim()}
        >
          Raise issue
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// The session's two stages as one strip: Started (the parent's start code,
// entered at the door — it proves arrival, so there is no separate Arrived
// step) and Completed. Done steps show a tick, the current one is the yellow
// button, the later one is greyed and inert.
const STAGES = [
  {
    key: 'started_at',
    action: 'arrive',
    label: 'Started',
    toast: 'Session started'
  },
  { key: 'completed_at', action: 'complete', label: 'Completed', toast: null }
]

const useStageStyles = makeStyles((theme) => ({
  strip: { display: 'inline-flex', alignItems: 'center', gap: 4 },
  done: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 12,
    color: theme.palette.text.secondary,
    padding: '0 6px',
    whiteSpace: 'nowrap'
  },
  tick: { fontSize: 14, color: theme.palette.success?.main || '#2e7d32' },
  later: {
    fontSize: 12,
    color: theme.palette.text.disabled,
    padding: '0 6px',
    whiteSpace: 'nowrap'
  },
  arrow: { color: theme.palette.text.disabled, fontSize: 12 },
  issue: { marginLeft: theme.spacing(1) }
}))

// The parent reads the 4-digit code off their app and the teacher types it
// here. It is the proof the teacher is at the right home with an adult there,
// so a correct code marks the session STARTED in one step — the server decides,
// and refuses a wrong or not-yet-active code with a reason.
const StartCodeDialog = ({ session, onClose, onDone }) => {
  const [code, setCode] = React.useState('')
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState(null)

  const submit = async () => {
    setBusy(true)
    setError(null)
    try {
      await atHomeSessionAction(session.uuid, { action: 'arrive', code })
      eventAlert('Session started', constants.SUCCESS)
      onDone()
      onClose()
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.message ||
          'Could not verify that code. Please try again.'
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Start code</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Ask {session.childInfo?.name || 'the child'}&apos;s parent for their
          4-digit start code and type it here.
        </Typography>
        <TextField
          autoFocus
          fullWidth
          margin="dense"
          label="4-digit code"
          value={code}
          onChange={(e) => {
            setCode(e.target.value.replace(/\D/g, '').slice(0, 4))
            if (error) setError(null)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && code.length === 4 && !busy) submit()
          }}
          inputProps={{ inputMode: 'numeric', maxLength: 4 }}
          error={Boolean(error)}
          helperText={error || ' '}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <AlfredButton onClick={submit} disabled={busy || code.length !== 4}>
          Verify &amp; start
        </AlfredButton>
      </DialogActions>
    </Dialog>
  )
}

const SessionActions = ({ s, onChanged, onIssue }) => {
  const classes = useStageStyles()
  const [completing, setCompleting] = React.useState(false)
  const [arriving, setArriving] = React.useState(false)
  const [noting, setNoting] = React.useState(false)
  const act = async (stage) => {
    if (stage.action === 'complete') {
      setCompleting(true)
      return
    }
    if (stage.action === 'arrive') {
      setArriving(true)
      return
    }
    try {
      await atHomeSessionAction(s.uuid, { action: stage.action })
      eventAlert(stage.toast, constants.SUCCESS)
      onChanged()
    } catch (error) {
      // toast from request.js
    }
  }
  const r = s.report || {}
  // Stage actions only on the session day, as in the teacher app (owner,
  // 2026-10-15). A past session left open is closed by ops from At Home →
  // Attended, not from here; a future one waits for its day.
  const open = s.status === 'booked' && s.date === todayLocal()
  const currentIdx = STAGES.findIndex((st) => !r[st.key])
  return (
    <>
      {open && (
        <span className={classes.strip}>
          {STAGES.map((st, i) => {
            const done = Boolean(r[st.key])
            const current = i === currentIdx
            const node = done ? (
              <span className={classes.done} title={fmtDateTime(r[st.key])}>
                <CheckIcon className={classes.tick} />
                {st.label}
              </span>
            ) : current ? (
              <AlfredButton size="small" onClick={() => act(st)}>
                {st.action === 'complete'
                  ? 'mark complete'
                  : st.action === 'arrive'
                  ? 'enter start code'
                  : `mark ${st.label.toLowerCase()}`}
              </AlfredButton>
            ) : (
              <span className={classes.later}>{st.label}</span>
            )
            return (
              <React.Fragment key={st.key}>
                {i > 0 && <span className={classes.arrow}>›</span>}
                {node}
              </React.Fragment>
            )
          })}
        </span>
      )}
      {r.started_at && s.status !== 'cancelled' && (
        <Button
          size="small"
          variant="outlined"
          className={classes.issue}
          onClick={() => setNoting(true)}
        >
          {r.photo_url ? 'Notes & photo' : 'Add notes & photo'}
        </Button>
      )}
      <Button
        size="small"
        variant="outlined"
        className={classes.issue}
        onClick={() => onIssue(s)}
      >
        Raise issue
      </Button>
      {noting && (
        <NotesPhotoDialog
          session={s}
          onClose={() => setNoting(false)}
          onDone={onChanged}
        />
      )}
      {arriving && (
        <StartCodeDialog
          session={s}
          onClose={() => setArriving(false)}
          onDone={onChanged}
        />
      )}
      {completing && (
        <CompleteDialog
          session={s}
          onClose={() => setCompleting(false)}
          onDone={onChanged}
        />
      )}
    </>
  )
}

const Sessions = ({ view }) => {
  const classes = useStyles()
  const [rows, setRows] = React.useState(null)
  const [error, setError] = React.useState(null)
  const [issueFor, setIssueFor] = React.useState(null)
  const [issueOpen, setIssueOpen] = React.useState(false)
  const load = React.useCallback(() => {
    setError(null)
    fetchMyAtHomeSessions(view)
      .then(setRows)
      .catch((e) => {
        setRows([])
        setError(
          e?.status === 403
            ? 'This page is for teachers and Alfred admins.'
            : 'Could not load sessions.'
        )
      })
  }, [view])
  React.useEffect(load, [load])
  if (!rows) return <Loading />
  return (
    <>
      {error ? (
        <div className={classes.empty}>{error}</div>
      ) : (
        <BookingsAgenda
          rows={rows}
          descending={view !== 'upcoming'}
          emptyText={
            view === 'upcoming'
              ? 'No upcoming bookings. Booked sessions from today onwards appear here with the child, parent and address.'
              : 'No past bookings yet. Sessions move here the day after they were scheduled; any still marked "Not closed" can be finished from here.'
          }
          renderActions={(b) => (
            <SessionActions
              s={b}
              onChanged={load}
              onIssue={(session) => {
                setIssueFor(session)
                setIssueOpen(true)
              }}
            />
          )}
        />
      )}
      <IssueDialog
        session={issueFor}
        open={issueOpen}
        onClose={() => setIssueOpen(false)}
        onDone={load}
      />
    </>
  )
}

// --- Issues --------------------------------------------------------------------

const useIssueStyles = makeStyles((theme) => ({
  head: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2)
  },
  group: { marginBottom: theme.spacing(3) },
  groupHead: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(1),
    margin: theme.spacing(0, 0, 1, 0.5)
  },
  groupLabel: { fontWeight: 700 },
  groupCount: { color: theme.palette.text.secondary },
  row: {
    display: 'grid',
    gridTemplateColumns: '110px minmax(240px, 2fr) minmax(200px, 1.2fr) 150px',
    alignItems: 'start',
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': { borderBottom: 'none' },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
      gap: theme.spacing(0.5)
    }
  },
  safetyRow: { boxShadow: `inset 4px 0 0 ${theme.palette.error.main}` },
  text: { fontSize: 14, lineHeight: 1.45 },
  resolution: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5)
  },
  context: { fontSize: 13, color: theme.palette.text.secondary },
  when: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    textAlign: 'right',
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('sm')]: { textAlign: 'left' }
  },
  empty: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(4),
    textAlign: 'center'
  }
}))

const IssueRow = ({ i }) => {
  const c = useIssueStyles()
  const b = i.bookingSummary
  return (
    <div className={`${c.row} ${i.type === 'safety' ? c.safetyRow : ''}`}>
      <Chip
        size="small"
        color={i.type === 'safety' ? 'secondary' : 'default'}
        label={i.type === 'safety' ? 'Safety' : 'Operational'}
      />
      <div>
        <div className={c.text}>{i.description}</div>
        {i.status === 'resolved' && (
          <div className={c.resolution}>
            Resolved {fmtDateTime(i.resolved_at)}
            {i.resolvedBy?.name ? ` by ${i.resolvedBy.name}` : ''}
            {i.resolution_notes ? ` — ${i.resolution_notes}` : ''}
          </div>
        )}
      </div>
      <div className={c.context}>
        {b ? (
          <>
            {b.category} · {b.child}
            <br />
            {fmtDate(b.date)}, {fmtTime(b.startTime)} · {b.apartment}
          </>
        ) : (
          'General (not tied to a session)'
        )}
      </div>
      <div className={c.when}>
        {fmtDateTime(i.created)}
        {i.raisedBy?.name ? (
          <>
            <br />
            {i.raisedBy.name}
          </>
        ) : null}
      </div>
    </div>
  )
}

const MyIssues = () => {
  const c = useIssueStyles()
  const [rows, setRows] = React.useState(null)
  const [open, setOpen] = React.useState(false)
  const load = React.useCallback(() => {
    fetchMyAtHomeIssues()
      .then(setRows)
      .catch(() => setRows([]))
  }, [])
  React.useEffect(load, [load])
  if (!rows) return <Loading />

  const byNewest = (a, b) => (b.created || '').localeCompare(a.created || '')
  // Open first; within open, safety before operational.
  const openRows = rows
    .filter((i) => i.status === 'open')
    .sort((a, b) =>
      a.type === b.type ? byNewest(a, b) : a.type === 'safety' ? -1 : 1
    )
  const resolvedRows = rows.filter((i) => i.status !== 'open').sort(byNewest)

  const Group = ({ label, list }) =>
    list.length ? (
      <div className={c.group}>
        <div className={c.groupHead}>
          <Typography variant="subtitle1" className={c.groupLabel}>
            {label}
          </Typography>
          <Typography variant="body2" className={c.groupCount}>
            {list.length}
          </Typography>
        </div>
        <Paper variant="outlined">
          {list.map((i) => (
            <IssueRow key={i.uuid} i={i} />
          ))}
        </Paper>
      </div>
    ) : null

  return (
    <>
      <div className={c.head}>
        <Typography variant="body2" color="textSecondary">
          Anything that got in the way of a session — access, materials,
          schedule, payment — or anything unsafe.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          disableElevation
          onClick={() => setOpen(true)}
        >
          Raise an issue
        </Button>
      </div>
      {rows.length === 0 ? (
        <div className={c.empty}>
          No issues raised. Use “Raise an issue” from a booking, or here for
          anything general.
        </div>
      ) : (
        <>
          <Group label="Open" list={openRows} />
          <Group label="Resolved" list={resolvedRows} />
        </>
      )}
      <IssueDialog
        session={null}
        open={open}
        onClose={() => setOpen(false)}
        onDone={load}
      />
    </>
  )
}

// --- Page ----------------------------------------------------------------------

const TABS = [
  { id: 'upcoming', label: 'Upcoming bookings' },
  { id: 'slots', label: 'Available slots' },
  { id: 'past', label: 'Past bookings' },
  { id: 'issues', label: 'Issues' }
]

const AtHomeTeacherPage = () => {
  const classes = useStyles()
  const [tab, setTab] = React.useState('upcoming')
  // Past sessions the teacher never closed: surfaced as a count on the tab
  // so they are not forgotten now that they no longer sit under Upcoming.
  const [unclosed, setUnclosed] = React.useState(0)
  React.useEffect(() => {
    fetchMyAtHomeSessions('past')
      .then((rows) =>
        setUnclosed(rows.filter((r) => r.status === 'booked').length)
      )
      .catch(() => setUnclosed(0))
  }, [tab])
  return (
    <div className={classes.root}>
      <Tabs
        className={classes.tabs}
        value={tab}
        onChange={(e, v) => setTab(v)}
        textColor="primary"
        variant="scrollable"
        scrollButtons="auto"
      >
        {TABS.map((t) => (
          <Tab
            key={t.id}
            value={t.id}
            label={
              t.id === 'past' && unclosed > 0 ? (
                <Badge
                  badgeContent={unclosed}
                  color="primary"
                  overlap="rectangular"
                  style={{ paddingRight: 14 }}
                >
                  {t.label}
                </Badge>
              ) : (
                t.label
              )
            }
            className={classes.tab}
          />
        ))}
      </Tabs>
      <div className={classes.body}>
        {tab === 'upcoming' && <Sessions view="upcoming" />}
        {tab === 'slots' && <SlotsCalendar status="all" />}
        {tab === 'past' && <Sessions view="past" />}
        {tab === 'issues' && <MyIssues />}
      </div>
    </div>
  )
}

export default AtHomeTeacherPage
